const API_TIMEOUT = 5000

export interface CompromisedPasswordResult {
  tag: 'NotFound' | 'Pwned' | 'Error'
  occurrences?: number
}

export type CheckCompromisedPassword = (
  password: string,
) => Promise<CompromisedPasswordResult>

// Check if a password has been compromised using the Have I Been Pwned API.
// Uses k-anonymity: only the first 5 chars of the SHA-1 hash are sent.
export const checkCompromisedPassword: CheckCompromisedPassword = async (
  password,
) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT)

  try {
    // Hash the password using Web Crypto API (browser-compatible)
    const encoder = new TextEncoder()
    const data = encoder.encode(password)
    const hashBuffer = await crypto.subtle.digest('SHA-1', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hash = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()

    const hashPrefix = hash.slice(0, 5)
    const hashSuffix = hash.slice(5)

    const response = await fetch(
      `https://api.pwnedpasswords.com/range/${hashPrefix}`,
      {
        method: 'GET',
        headers: {
          'User-Agent': 'Moment-Password-Checker/1.0',
          Accept: 'text/plain',
          'Cache-Control': 'no-cache',
        },
        signal: controller.signal,
      },
    )

    clearTimeout(timeoutId)

    if (!response.ok) {
      return { tag: 'Error' }
    }

    const text = await response.text()

    if (!text || typeof text !== 'string') {
      return { tag: 'Error' }
    }

    const entries = text.split('\n').flatMap((line) => {
      const [suffix, occurrences] = line.split(':')
      if (!(suffix && occurrences)) {
        return []
      }

      const count = Number.parseInt(occurrences.trim(), 10)
      if (Number.isNaN(count) || count < 0) {
        return []
      }

      return [{ suffix: suffix.trim().toUpperCase(), occurrences: count }]
    })

    const matchingEntry = entries.find(
      (entry) => entry.suffix === hashSuffix.toUpperCase(),
    )

    if (!matchingEntry) {
      return { tag: 'NotFound' }
    }

    return { tag: 'Pwned', occurrences: matchingEntry.occurrences }
  } catch {
    clearTimeout(timeoutId)
    // On error, return Error tag but don't block the user.
    return { tag: 'Error' }
  }
}
